import React, { useState, useEffect, useRef } from 'react';
import { X, FileText, Save } from 'lucide-react';
import { useCommunicationStore } from '../../store/communicationStore';
import { useAuthStore } from '../../store/authStore';
import { EmailTemplate, CreateEmailTemplateData } from '../../services/communication.service';

interface AddTemplateModalProps {
    isOpen: boolean;
    onClose: () => void;
    editingTemplate: EmailTemplate | null;
}

const CATEGORIES = [
    { value: 'outreach', label: 'Initial Outreach' },
    { value: 'follow_up', label: 'Follow Up' },
    { value: 'proposal', label: 'Proposal' },
    { value: 'contract', label: 'Contract' },
    { value: 'invoice', label: 'Invoice' },
    { value: 'thank_you', label: 'Thank You' },
];

const AddTemplateModal: React.FC<AddTemplateModalProps> = ({
    isOpen,
    onClose,
    editingTemplate,
}) => {
    const { createTemplate, updateTemplate, isLoading } = useCommunicationStore();
    const { user, team } = useAuthStore();

    const [formData, setFormData] = useState<CreateEmailTemplateData>({
        name: '',
        category: 'outreach',
        subject: '',
        bodyHtml: '',
        variables: [],
    });

    const [extractedVariables, setExtractedVariables] = useState<string[]>([]);
    const [lastFocusedField, setLastFocusedField] = useState<'subject' | 'body'>('body');
    const [cursorPosition, setCursorPosition] = useState<{ subject: number; body: number }>({ subject: 0, body: 0 });

    const subjectInputRef = useRef<HTMLInputElement>(null);
    const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (editingTemplate) {
            setFormData({
                name: editingTemplate.name,
                category: editingTemplate.category,
                subject: editingTemplate.subject,
                bodyHtml: editingTemplate.bodyHtml,
                variables: editingTemplate.variables,
            });
        } else {
            resetForm();
        }
    }, [editingTemplate, isOpen]);

    // Extract variables from body
    useEffect(() => {
        const pattern = /\{\{([^}]+)\}\}/g;
        const found: string[] = [];
        let match;
        const content = formData.subject + ' ' + formData.bodyHtml;
        while ((match = pattern.exec(content)) !== null) {
            if (!found.includes(match[1].trim())) {
                found.push(match[1].trim());
            }
        }
        setExtractedVariables(found);
    }, [formData.subject, formData.bodyHtml]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const dataToSave = {
                ...formData,
                variables: extractedVariables,
            };

            if (editingTemplate) {
                await updateTemplate(editingTemplate.id, dataToSave);
            } else {
                await createTemplate(dataToSave);
            }
            onClose();
            resetForm();
        } catch (error) {
            console.error('Failed to save template:', error);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            category: 'outreach',
            subject: '',
            bodyHtml: '',
            variables: [],
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
                            <FileText className="w-5 h-5 text-white" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                            {editingTemplate ? 'Edit Template' : 'Create Template'}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
                    {/* Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Template Name *
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            required
                            placeholder="e.g., Initial Brand Outreach"
                            className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                        />
                    </div>

                    {/* Category */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Category *
                        </label>
                        <select
                            value={formData.category}
                            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                            required
                            className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                        >
                            {CATEGORIES.map(cat => (
                                <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Subject */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Subject Line *
                        </label>
                        <input
                            ref={subjectInputRef}
                            type="text"
                            value={formData.subject}
                            onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                            onFocus={() => setLastFocusedField('subject')}
                            onSelect={(e) => setCursorPosition(prev => ({ ...prev, subject: (e.target as HTMLInputElement).selectionStart || 0 }))}
                            onKeyUp={(e) => setCursorPosition(prev => ({ ...prev, subject: (e.target as HTMLInputElement).selectionStart || 0 }))}
                            onClick={(e) => setCursorPosition(prev => ({ ...prev, subject: (e.target as HTMLInputElement).selectionStart || 0 }))}
                            required
                            placeholder="e.g., Partnership Opportunity with {{brand.name}}"
                            className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                        />
                    </div>

                    {/* Body */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Email Body *
                        </label>
                        <textarea
                            ref={bodyTextareaRef}
                            value={formData.bodyHtml}
                            onChange={(e) => setFormData(prev => ({ ...prev, bodyHtml: e.target.value }))}
                            onFocus={() => setLastFocusedField('body')}
                            onSelect={(e) => setCursorPosition(prev => ({ ...prev, body: (e.target as HTMLTextAreaElement).selectionStart || 0 }))}
                            onKeyUp={(e) => setCursorPosition(prev => ({ ...prev, body: (e.target as HTMLTextAreaElement).selectionStart || 0 }))}
                            onClick={(e) => setCursorPosition(prev => ({ ...prev, body: (e.target as HTMLTextAreaElement).selectionStart || 0 }))}
                            required
                            rows={10}
                            placeholder={`Hi {{contact.firstName}},

I'm reaching out about a potential partnership opportunity...

Best regards,
{{user.firstName}}`}
                            className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-none font-mono text-sm"
                        />
                    </div>

                    {/* Extracted Variables */}
                    {extractedVariables.length > 0 && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Detected Variables
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {extractedVariables.map(v => (
                                    <span
                                        key={v}
                                        className="px-3 py-1.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-lg text-sm font-mono"
                                    >
                                        {`{{${v}}}`}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Variable Hints */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
                        <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                            Available Variables
                        </h4>
                        <div className="flex flex-wrap gap-2 text-xs">
                            {/* Partnership variables - always show as placeholders */}
                            {['contact.firstName', 'contact.email', 'brand.name'].map(v => (
                                <code
                                    key={v}
                                    className="px-2 py-1 bg-blue-100 dark:bg-blue-800/30 text-blue-700 dark:text-blue-300 rounded cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors"
                                    onClick={() => {
                                        const variableText = `{{${v}}}`;
                                        if (lastFocusedField === 'subject') {
                                            const pos = cursorPosition.subject;
                                            const before = formData.subject.substring(0, pos);
                                            const after = formData.subject.substring(pos);
                                            setFormData(prev => ({ ...prev, subject: before + variableText + after }));
                                            setCursorPosition(prev => ({ ...prev, subject: pos + variableText.length }));
                                            setTimeout(() => {
                                                if (subjectInputRef.current) {
                                                    subjectInputRef.current.focus();
                                                    subjectInputRef.current.setSelectionRange(pos + variableText.length, pos + variableText.length);
                                                }
                                            }, 10);
                                        } else {
                                            const pos = cursorPosition.body;
                                            const before = formData.bodyHtml.substring(0, pos);
                                            const after = formData.bodyHtml.substring(pos);
                                            setFormData(prev => ({ ...prev, bodyHtml: before + variableText + after }));
                                            setCursorPosition(prev => ({ ...prev, body: pos + variableText.length }));
                                            setTimeout(() => {
                                                if (bodyTextareaRef.current) {
                                                    bodyTextareaRef.current.focus();
                                                    bodyTextareaRef.current.setSelectionRange(pos + variableText.length, pos + variableText.length);
                                                }
                                            }, 10);
                                        }
                                    }}
                                >
                                    {`{{${v}}}`}
                                </code>
                            ))}
                            {/* User variables - show actual values */}
                            {user?.firstName && (
                                <code
                                    className="px-2 py-1 bg-green-100 dark:bg-green-800/30 text-green-700 dark:text-green-300 rounded cursor-pointer hover:bg-green-200 dark:hover:bg-green-800/50 transition-colors"
                                    onClick={() => {
                                        const actualValue = user.firstName;
                                        if (lastFocusedField === 'subject') {
                                            const pos = cursorPosition.subject;
                                            const newPos = pos + actualValue.length;
                                            setFormData(prev => ({ ...prev, subject: prev.subject.substring(0, pos) + actualValue + prev.subject.substring(pos) }));
                                            setCursorPosition(prev => ({ ...prev, subject: newPos }));
                                            setTimeout(() => {
                                                if (subjectInputRef.current) {
                                                    subjectInputRef.current.focus();
                                                    subjectInputRef.current.setSelectionRange(newPos, newPos);
                                                }
                                            }, 10);
                                        } else {
                                            const pos = cursorPosition.body;
                                            const newPos = pos + actualValue.length;
                                            setFormData(prev => ({ ...prev, bodyHtml: prev.bodyHtml.substring(0, pos) + actualValue + prev.bodyHtml.substring(pos) }));
                                            setCursorPosition(prev => ({ ...prev, body: newPos }));
                                            setTimeout(() => {
                                                if (bodyTextareaRef.current) {
                                                    bodyTextareaRef.current.focus();
                                                    bodyTextareaRef.current.setSelectionRange(newPos, newPos);
                                                }
                                            }, 10);
                                        }
                                    }}
                                >
                                    {user.firstName}
                                </code>
                            )}
                            {user?.email && (
                                <code
                                    className="px-2 py-1 bg-green-100 dark:bg-green-800/30 text-green-700 dark:text-green-300 rounded cursor-pointer hover:bg-green-200 dark:hover:bg-green-800/50 transition-colors"
                                    onClick={() => {
                                        const actualValue = user.email;
                                        if (lastFocusedField === 'subject') {
                                            const pos = cursorPosition.subject;
                                            const newPos = pos + actualValue.length;
                                            setFormData(prev => ({ ...prev, subject: prev.subject.substring(0, pos) + actualValue + prev.subject.substring(pos) }));
                                            setCursorPosition(prev => ({ ...prev, subject: newPos }));
                                            setTimeout(() => {
                                                if (subjectInputRef.current) {
                                                    subjectInputRef.current.focus();
                                                    subjectInputRef.current.setSelectionRange(newPos, newPos);
                                                }
                                            }, 10);
                                        } else {
                                            const pos = cursorPosition.body;
                                            const newPos = pos + actualValue.length;
                                            setFormData(prev => ({ ...prev, bodyHtml: prev.bodyHtml.substring(0, pos) + actualValue + prev.bodyHtml.substring(pos) }));
                                            setCursorPosition(prev => ({ ...prev, body: newPos }));
                                            setTimeout(() => {
                                                if (bodyTextareaRef.current) {
                                                    bodyTextareaRef.current.focus();
                                                    bodyTextareaRef.current.setSelectionRange(newPos, newPos);
                                                }
                                            }, 10);
                                        }
                                    }}
                                >
                                    {user.email}
                                </code>
                            )}
                            {/* Team name - show actual value */}
                            {team?.name && (
                                <code
                                    className="px-2 py-1 bg-green-100 dark:bg-green-800/30 text-green-700 dark:text-green-300 rounded cursor-pointer hover:bg-green-200 dark:hover:bg-green-800/50 transition-colors"
                                    onClick={() => {
                                        const actualValue = team.name;
                                        if (lastFocusedField === 'subject') {
                                            const pos = cursorPosition.subject;
                                            const newPos = pos + actualValue.length;
                                            setFormData(prev => ({ ...prev, subject: prev.subject.substring(0, pos) + actualValue + prev.subject.substring(pos) }));
                                            setCursorPosition(prev => ({ ...prev, subject: newPos }));
                                            setTimeout(() => {
                                                if (subjectInputRef.current) {
                                                    subjectInputRef.current.focus();
                                                    subjectInputRef.current.setSelectionRange(newPos, newPos);
                                                }
                                            }, 10);
                                        } else {
                                            const pos = cursorPosition.body;
                                            const newPos = pos + actualValue.length;
                                            setFormData(prev => ({ ...prev, bodyHtml: prev.bodyHtml.substring(0, pos) + actualValue + prev.bodyHtml.substring(pos) }));
                                            setCursorPosition(prev => ({ ...prev, body: newPos }));
                                            setTimeout(() => {
                                                if (bodyTextareaRef.current) {
                                                    bodyTextareaRef.current.focus();
                                                    bodyTextareaRef.current.setSelectionRange(newPos, newPos);
                                                }
                                            }, 10);
                                        }
                                    }}
                                >
                                    {team.name}
                                </code>
                            )}
                        </div>
                        <p className="text-xs text-blue-600/70 dark:text-blue-400/70 mt-2">
                            Blue = placeholder • <span className="text-green-600 dark:text-green-400">Green = inserts actual value</span>
                        </p>
                    </div>
                </form>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isLoading || !formData.name || !formData.subject || !formData.bodyHtml}
                        className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Save className="w-4 h-4" />
                        {isLoading ? 'Saving...' : editingTemplate ? 'Update Template' : 'Create Template'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddTemplateModal;
